import { parseIfValid } from './json';

/**
 * Info POS del comercio logueado, persistida en localStorage al iniciar sesión.
 * Estructura (commerce_info): { id, name, pos, pos_admin }
 *
 *  - pos: el comercio puede crear / editar sus propios productos y grupos.
 *  - pos_admin: además puede administrar los de sus comercios hijos
 *    (los que aparecen en su listado de comercios).
 *
 * @returns {object|null}
 */
export function getPosInfo() {
  return parseIfValid(localStorage.getItem('pos_info'), false) || null;
}

/**
 * Id del comercio de la sesión.
 *
 * Se toma del `commerce` que guarda el login (de `users/get_info`), que es la
 * fuente con la que se consulta `commerce_info`: así el id no depende de que
 * esa respuesta traiga info. Importa porque hay pantallas que preguntan por el
 * comercio aunque no administre productos —el detalle de una venta hecha en la
 * terminal—, y un comercio sin POS puede no traer `pos_info`.
 *
 * @returns {number|null}
 */
export function getPosCommerceId() {
  const id = parseIfValid(localStorage.getItem('commerce'), false)
    ?? getPosInfo()?.id;
  const parsed = Number(id);

  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/**
 * Nombre del comercio de la sesión.
 *
 * @returns {string|null}
 */
export function getPosCommerceName() {
  return getPosInfo()?.name ?? null;
}

/**
 * El comercio administra sus propios productos y grupos.
 *
 * @returns {boolean}
 */
export function hasPos() {
  return getPosInfo()?.pos === true;
}

/**
 * El comercio administra los productos y grupos de sus comercios hijos.
 *
 * @returns {boolean}
 */
export function isPosAdmin() {
  return getPosInfo()?.pos_admin === true;
}

/**
 * Tiene acceso a la sección POS (propio y/o como administrador de hijos).
 *
 * @returns {boolean}
 */
export function canUsePos() {
  return hasPos() || isPosAdmin();
}
